// @ts-nocheck
/**
 * MinimizedUrlPanelsDock Component
 *
 * Displays minimized URL preview panels in a dock at the bottom of the screen.
 * Persists at the AppShell level and is visible from ANY page.
 *
 * Features:
 * - Compact pill-style minimized panels with title preview
 * - Click to restore panel as global overlay
 * - Double-click title to rename
 * - Right-click context menu for rename/close
 * - Close button to remove panels entirely
 * - Animated entrance/exit with Framer Motion
 * - Respects Dynamics theme
 * - Positioned above UserStickies dock (to avoid overlap)
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ExternalLink, X, Pencil, Trash2 } from 'lucide-react';
import { Portal } from '@shared/components/ui/Portal';
import { useMinimizedUrlPanelsStore, type MinimizedUrlPanel } from '@shared/store/minimizedUrlPanelsStore';
import { useUIStore } from '@shared/store/uiStore';
import { useIsMobile } from '@shared/hooks/useIsMobile';
import { useAppPanelStore } from '@shared/store/useAppPanelStore';
import { APP_PANEL_TASKBAR_HEIGHT } from './AppPanelTaskbar';

/**
 * Animation variants for pills - compact version
 */
const pillVariants = {
  hidden: { opacity: 0, scale: 0.8, y: 10 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 500, damping: 30 },
  },
  exit: { opacity: 0, scale: 0.8, y: 10, transition: { duration: 0.12 } },
  hover: { scale: 1.03, y: -1 },
  tap: { scale: 0.97 },
};

/**
 * Reduced motion variants
 */
const reducedMotionVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.1 } },
  exit: { opacity: 0, transition: { duration: 0.1 } },
  hover: {},
  tap: {},
};

/**
 * Context menu position
 */
interface ContextMenuPosition {
  x: number;
  y: number;
  panelId: string;
}

interface MinimizedUrlPillProps {
  panel: MinimizedUrlPanel;
  onRestore: () => void;
  onRemove: () => void;
  onRename: (newTitle: string) => void;
  onContextMenu: (e: React.MouseEvent, panelId: string) => void;
  isDynamicsTheme: boolean;
  isMobile: boolean;
}

/**
 * Individual minimized URL pill - compact design
 */
const MinimizedUrlPill: React.FC<MinimizedUrlPillProps> = ({
  panel,
  onRestore,
  onRemove,
  onRename,
  onContextMenu,
  isDynamicsTheme,
  isMobile,
}) => {
  const prefersReducedMotion = useReducedMotion();
  const variants = prefersReducedMotion ? reducedMotionVariants : pillVariants;
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Use custom title if set, otherwise fall back to default title
  const effectiveTitle = panel.customTitle || panel.title;

  // Truncate title for display - shorter for compact design
  const displayTitle =
    effectiveTitle.length > (isMobile ? 12 : 18)
      ? `${effectiveTitle.substring(0, isMobile ? 12 : 18)}...`
      : effectiveTitle;

  // Handle double-click to edit
  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setEditValue(effectiveTitle);
    setIsEditing(true);
  }, [effectiveTitle]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Handle edit submission
  const handleEditSubmit = useCallback(() => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== effectiveTitle) {
      onRename(trimmed);
    }
    setIsEditing(false);
  }, [editValue, effectiveTitle, onRename]);

  // Handle edit cancel
  const handleEditCancel = useCallback(() => {
    setIsEditing(false);
    setEditValue('');
  }, []);

  // Handle key events in edit mode
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleEditSubmit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleEditCancel();
    }
  }, [handleEditSubmit, handleEditCancel]);

  // Handle right-click
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onContextMenu(e, panel.id);
  }, [onContextMenu, panel.id]);

  return (
    <motion.div
      variants={variants}
      initial="hidden"
      animate="visible"
      exit="exit"
      whileHover={isEditing ? undefined : 'hover'}
      whileTap={isEditing ? undefined : 'tap'}
      className={`
        relative flex items-center gap-2
        rounded-md shadow-md border
        transition-all
        ${isEditing ? '' : 'cursor-pointer hover:shadow-lg'}
        ${isMobile ? 'px-2 py-1' : 'px-2.5 py-1.5'}
        ${isMobile ? 'min-w-[100px] max-w-[140px]' : 'min-w-[120px] max-w-[180px]'}
        ${isDynamicsTheme
          ? 'bg-dark-surface border-dark-border/50 hover:bg-dark-surface/90'
          : 'bg-dark-surface border-dark-border/50 hover:bg-dark-surface/90'
        }
      `}
      onClick={isEditing ? undefined : onRestore}
      onContextMenu={handleContextMenu}
      role="button"
      aria-label={`Restore preview: ${effectiveTitle}`}
      tabIndex={isEditing ? -1 : 0}
      onKeyDown={(e) => {
        if (!isEditing && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onRestore();
        }
      }}
      data-testid={`minimized-panel-${panel.id}`}
    >
      {/* Link icon - smaller */}
      <div
        className={`
          flex-shrink-0 w-5 h-5 rounded flex items-center justify-center
          ${isDynamicsTheme ? 'bg-blue-100 text-brand-400' : 'bg-blue-900/40 text-blue-400'}
        `}
      >
        <ExternalLink size={12} />
      </div>

      {/* Title - editable on double-click */}
      <div className="flex-1 min-w-0" onDoubleClick={handleDoubleClick}>
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleEditSubmit}
            onKeyDown={handleKeyDown}
            onClick={(e) => e.stopPropagation()}
            className={`
              w-full text-xs font-medium px-1 py-0.5 rounded
              outline-none border
              ${isDynamicsTheme
                ? 'bg-dark-surface border-brand-500 text-white focus:ring-1 focus:ring-brand-400'
                : 'bg-dark-input border-brand-500 text-white focus:ring-1 focus:ring-brand-400'
              }
            `}
            aria-label="Edit panel title"
            data-testid={`panel-title-input-${panel.id}`}
          />
        ) : (
          <p
            className={`text-xs font-medium truncate ${isDynamicsTheme ? 'text-white' : 'text-white'}`}
            title={`${effectiveTitle} (double-click to rename)`}
          >
            {displayTitle}
          </p>
        )}
      </div>

      {/* Close button - smaller */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className={`
          flex-shrink-0 p-0.5 rounded transition-all duration-150
          ${isDynamicsTheme
            ? 'text-dark-muted hover:text-dark-muted hover:bg-dark-surface/40'
            : 'text-dark-muted hover:text-white hover:bg-dark-surface/40'
          }
        `}
        aria-label={`Close ${effectiveTitle}`}
        data-testid={`close-panel-${panel.id}`}
      >
        <X size={12} />
      </button>
    </motion.div>
  );
};

/**
 * Context menu component for right-click actions
 */
interface ContextMenuProps {
  position: ContextMenuPosition;
  panel: MinimizedUrlPanel;
  onClose: () => void;
  onRename: () => void;
  onRemove: () => void;
  isDynamicsTheme: boolean;
}

const ContextMenu: React.FC<ContextMenuProps> = ({
  position,
  panel,
  onClose,
  onRename,
  onRemove,
  isDynamicsTheme,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const effectiveTitle = panel.customTitle || panel.title;

  return (
    <Portal>
      <motion.div
        ref={menuRef}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.1 }}
        className={`
          fixed z-[200] rounded-xl shadow-2xl shadow-black/20 border py-1 min-w-[140px]
          ${isDynamicsTheme
            ? 'bg-dark-surface border-dark-border/30'
            : 'bg-dark-surface border-dark-border/30'
          }
        `}
        style={{
          left: Math.min(position.x, window.innerWidth - 160),
          top: Math.min(position.y, window.innerHeight - 100),
        }}
        role="menu"
        aria-label={`Actions for ${effectiveTitle}`}
        data-testid="panel-context-menu"
      >
        <button
          type="button"
          onClick={() => {
            onRename();
            onClose();
          }}
          className={`
            w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left
            ${isDynamicsTheme
              ? 'text-dark-text hover:bg-dark-input'
              : 'text-white/90 hover:bg-dark-input'
            }
          `}
          role="menuitem"
          data-testid="context-menu-rename"
        >
          <Pencil size={14} />
          Rename
        </button>
        <button
          type="button"
          onClick={() => {
            onRemove();
            onClose();
          }}
          className={`
            w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left
            text-red-500 hover:bg-red-900/30
          `}
          role="menuitem"
          data-testid="context-menu-close"
        >
          <Trash2 size={14} />
          Close
        </button>
      </motion.div>
    </Portal>
  );
};

/**
 * MinimizedUrlPanelsDock - Container for minimized URL panels
 */
export const MinimizedUrlPanelsDock: React.FC = () => {
  const { minimizedPanels, isDockVisible, restorePanel, removePanel, renamePanelTitle } = useMinimizedUrlPanelsStore();
  const themeClass = useUIStore((state) => state.themeClass);
  const isMobile = useIsMobile();
  const isDynamicsTheme = themeClass === 'theme-dynamics';

  // Check if AppPanel taskbar is visible to adjust positioning
  const isTaskbarVisible = useAppPanelStore((state) => state.isTaskbarVisible);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<ContextMenuPosition | null>(null);
  // Track which panel should enter edit mode after context menu closes
  const [editingPanelId, setEditingPanelId] = useState<string | null>(null);

  // Handle context menu open
  const handleContextMenu = useCallback((e: React.MouseEvent, panelId: string) => {
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      panelId,
    });
  }, []);

  // Handle context menu close
  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  // Handle rename from context menu - trigger edit mode
  const handleRenameFromContext = useCallback(() => {
    if (contextMenu) {
      setEditingPanelId(contextMenu.panelId);
    }
  }, [contextMenu]);

  // Get panel for context menu
  const contextMenuPanel = contextMenu
    ? minimizedPanels.find((p) => p.id === contextMenu.panelId)
    : null;

  // Don't render if inside an iframe (prevents showing inside other panels)
  const isInIframe = typeof window !== 'undefined' && window.self !== window.top;

  // Don't render if no minimized panels, dock is hidden, or inside iframe
  if (minimizedPanels.length === 0 || !isDockVisible || isInIframe) {
    return null;
  }

  // Calculate bottom position: base (56px for stickies) + taskbar height if visible
  const baseBottom = 56;
  const taskbarOffset = isTaskbarVisible ? APP_PANEL_TASKBAR_HEIGHT : 0;
  const bottomPosition = baseBottom + taskbarOffset;

  return (
    <Portal>
      <div
        className={`
          fixed z-[150]
          flex flex-wrap justify-center gap-1.5
          transition-[bottom] duration-200 ease-out
          ${isMobile
            ? 'left-2 right-2'
            : 'left-1/2 -translate-x-1/2'
          }
        `}
        style={{
          bottom: `calc(${bottomPosition}px + env(safe-area-inset-bottom))`,
        }}
        role="region"
        aria-label="Minimized URL previews"
        data-testid="minimized-panels-dock"
      >
        <AnimatePresence mode="popLayout">
          {minimizedPanels.map((panel) => (
            <MinimizedUrlPill
              key={panel.id}
              panel={panel}
              onRestore={() => restorePanel(panel.id)}
              onRemove={() => removePanel(panel.id)}
              onRename={(newTitle) => renamePanelTitle(panel.id, newTitle)}
              onContextMenu={handleContextMenu}
              isDynamicsTheme={isDynamicsTheme}
              isMobile={isMobile}
            />
          ))}
        </AnimatePresence>
      </div>

      {/* Context Menu */}
      <AnimatePresence>
        {contextMenu && contextMenuPanel && (
          <ContextMenu
            position={contextMenu}
            panel={contextMenuPanel}
            onClose={handleCloseContextMenu}
            onRename={handleRenameFromContext}
            onRemove={() => {
              removePanel(contextMenu.panelId);
              handleCloseContextMenu();
            }}
            isDynamicsTheme={isDynamicsTheme}
          />
        )}
      </AnimatePresence>
    </Portal>
  );
};

export default MinimizedUrlPanelsDock;

